import { Add, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Slider,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import EmailTemplateEditor from "../../../components/EmailTemplateEditor";
import PopOutTextInput from "../../../components/PopOutTextInput";
import {
  AB_TEST_DEFAULT_VARIANT_NAME,
  SEQUENCE_ACTION_TYPE_EMAIL,
  SEQUENCE_ACTION_TYPE_SMS,
  SEQUENCE_ACTION_TYPE_VOICE_MESSAGE,
  SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE,
} from "../../../constants";

// TypeScript interfaces
export interface SequenceStepVariant {
  id: string;
  sequenceStepId?: string;
  name: string;
  order: number;
  subject?: string;
  content: string;
  weight: number;
  isControl: boolean;
  isActive: boolean;
  aiPersonalized: boolean;
  aiPersonalizedInstructions?: string;
}

interface ABTestEditorProps {
  step: any;
  variants: SequenceStepVariant[];
  onVariantChange: (variants: SequenceStepVariant[]) => void;
  actionType: string;
  companyId: string;
  sequenceId?: string;
  getToken: () => Promise<string>;
  customVariableFields?: { key: string; label: string }[];
  schedulingIntegrations?: any[];
  heygenIntegrations?: any[];
  onAISuccess?: (message: string) => void;
  onAIError?: (error: string) => void;
}

const ABTestEditor: React.FC<ABTestEditorProps> = ({
  step,
  variants,
  onVariantChange,
  actionType,
  companyId,
  sequenceId,
  getToken,
  customVariableFields = [],
  schedulingIntegrations = [],
  heygenIntegrations = [],
  onAISuccess,
  onAIError,
}) => {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);

  // Ensure selected index is valid
  const safeIndex = Math.min(selectedVariantIndex, Math.max(0, variants.length - 1));
  const selectedVariant = variants[safeIndex];

  // Auto-balance weights when one changes
  const handleWeightChange = (variantId: string, newWeight: number) => {
    const updatedVariants = [...variants];
    const targetIndex = updatedVariants.findIndex((v) => v.id === variantId);

    if (targetIndex === -1) return;

    // Clamp to valid range
    newWeight = Math.max(0, Math.min(100, newWeight));

    // Get other active variants to distribute the difference
    const otherVariants = updatedVariants.filter(
      (v, i) => i !== targetIndex && v.isActive
    );

    if (otherVariants.length === 0) {
      // Only one variant, set to 100%
      updatedVariants[targetIndex].weight = 100;
    } else {
      // Set new weight
      updatedVariants[targetIndex].weight = newWeight;

      // Calculate remaining weight to distribute
      const remainingWeight = 100 - newWeight;
      const totalOtherWeight = otherVariants.reduce(
        (sum, v) => sum + v.weight,
        0
      );

      // Distribute proportionally among other variants
      let distributed = 0;
      otherVariants.forEach((v, idx) => {
        const variantIndex = updatedVariants.findIndex(
          (uv) => uv.id === v.id
        );
        if (totalOtherWeight > 0) {
          const proportion = v.weight / totalOtherWeight;
          const newOtherWeight =
            idx === otherVariants.length - 1
              ? remainingWeight - distributed
              : Math.round(remainingWeight * proportion);
          updatedVariants[variantIndex].weight = Math.max(0, newOtherWeight);
          distributed += updatedVariants[variantIndex].weight;
        } else {
          // Equal distribution if all others are 0
          const equalWeight = Math.round(remainingWeight / otherVariants.length);
          updatedVariants[variantIndex].weight = equalWeight;
        }
      });
    }

    onVariantChange(updatedVariants);
  };

  const handleAddVariant = () => {
    const newVariant: SequenceStepVariant = {
      id: uuidv4(),
      name: `Variant ${String.fromCharCode(65 + variants.length)}`, // A, B, C, etc.
      order: variants.length + 1,
      subject: actionType === SEQUENCE_ACTION_TYPE_EMAIL ? step.subject : undefined,
      content: step.content || "",
      weight: 0,
      isControl: false,
      isActive: true,
      aiPersonalized: step.aiPersonalized || false,
      aiPersonalizedInstructions: step.aiPersonalizedInstructions || "",
    };

    // Redistribute weights
    const newVariants = [...variants, newVariant];
    const activeCount = newVariants.filter((v) => v.isActive).length;
    const equalWeight = Math.floor(100 / activeCount);
    const remainder = 100 - equalWeight * activeCount;

    newVariants.forEach((v, idx) => {
      if (v.isActive) {
        v.weight = equalWeight + (idx === 0 ? remainder : 0);
      }
    });

    onVariantChange(newVariants);
    setSelectedVariantIndex(newVariants.length - 1);
  };

  const handleDeleteVariant = (variantId: string) => {
    if (variants.length <= 1) return; // Can't delete the last variant

    const deleteIndex = variants.findIndex((v) => v.id === variantId);
    const newVariants = variants.filter((v) => v.id !== variantId);

    // Redistribute weights
    const activeVariants = newVariants.filter((v) => v.isActive);
    const totalCurrentWeight = activeVariants.reduce(
      (sum, v) => sum + v.weight,
      0
    );

    if (totalCurrentWeight !== 100 && activeVariants.length > 0) {
      const scale = 100 / totalCurrentWeight;
      let distributed = 0;
      activeVariants.forEach((v, idx) => {
        if (idx === activeVariants.length - 1) {
          v.weight = 100 - distributed;
        } else {
          v.weight = Math.round(v.weight * scale);
          distributed += v.weight;
        }
      });
    }

    onVariantChange(newVariants);
    // Adjust selected index if needed
    if (deleteIndex <= selectedVariantIndex && selectedVariantIndex > 0) {
      setSelectedVariantIndex(selectedVariantIndex - 1);
    }
  };

  const handleVariantFieldChange = (
    variantId: string,
    field: keyof SequenceStepVariant,
    value: any
  ) => {
    const updatedVariants = variants.map((v) =>
      v.id === variantId ? { ...v, [field]: value } : v
    );
    onVariantChange(updatedVariants);
  };

  // Weight distribution visualization
  const renderWeightDistribution = () => {
    // Use primary color with different shades for each variant
    const getVariantStyles = (idx: number) => {
      const shades = [
        { bg: "primary.dark" },
        { bg: "primary.main" },
        { bg: "primary.light" },
        { bg: "primary.main", opacity: 0.6 },
        { bg: "primary.light", opacity: 0.7 },
        { bg: "primary.dark", opacity: 0.5 },
      ];
      return shades[idx % shades.length];
    };

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
          Traffic Distribution
        </Typography>
        <Box
          sx={{
            display: "flex",
            height: 8,
            borderRadius: 1,
            overflow: "hidden",
            backgroundColor: "divider",
          }}
        >
          {variants
            .filter((v) => v.isActive && v.weight > 0)
            .map((variant, idx) => {
              const styles = getVariantStyles(idx);
              return (
                <Tooltip
                  key={variant.id}
                  title={`${variant.name}: ${variant.weight}%`}
                >
                  <Box
                    sx={{
                      width: `${variant.weight}%`,
                      backgroundColor: styles.bg,
                      opacity: styles.opacity || 1,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Tooltip>
              );
            })}
        </Box>
        <Box sx={{ display: "flex", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
          {variants
            .filter((v) => v.isActive)
            .map((variant, idx) => {
              const styles = getVariantStyles(idx);
              return (
                <Box
                  key={variant.id}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: styles.bg,
                      opacity: styles.opacity || 1,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {variant.name}: {variant.weight}%
                  </Typography>
                </Box>
              );
            })}
        </Box>
      </Box>
    );
  };

  const getContentLabel = () => {
    switch (actionType) {
      case SEQUENCE_ACTION_TYPE_SMS:
        return "SMS Message";
      case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
        return "Voice Message Script";
      case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
        return "LinkedIn Message";
      default:
        return "Content";
    }
  };

  if (!selectedVariant) {
    return null;
  }

  return (
    <Box>
      {renderWeightDistribution()}

      {/* Tabs for variant selection */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center" }}>
        <Tabs
          value={safeIndex}
          onChange={(_, newValue) => setSelectedVariantIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ flex: 1 }}
        >
          {variants.map((variant, index) => (
            <Tab
              key={variant.id}
              label={variant.name}
            />
          ))}
        </Tabs>
        <Button
          startIcon={<Add />}
          onClick={handleAddVariant}
          size="small"
          sx={{ ml: 1, minWidth: "auto", whiteSpace: "nowrap" }}
        >
          Add Variant
        </Button>
      </Box>

      {/* Variant Editor */}
      <Box sx={{ pt: 2 }}>
        {/* Variant Header with Name and Delete */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <TextField
            label="Variant Name"
            value={selectedVariant.name}
            onChange={(e) =>
              handleVariantFieldChange(selectedVariant.id, "name", e.target.value)
            }
            size="small"
            sx={{ width: 200 }}
          />
          {variants.length > 1 && (
            <Tooltip title="Delete this variant">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteVariant(selectedVariant.id)}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Weight Slider */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Traffic Weight
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Slider
              value={selectedVariant.weight}
              onChange={(_, value) =>
                handleWeightChange(selectedVariant.id, value as number)
              }
              min={0}
              max={100}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{ flex: 1 }}
            />
            <TextField
              value={selectedVariant.weight}
              onChange={(e) =>
                handleWeightChange(
                  selectedVariant.id,
                  parseInt(e.target.value) || 0
                )
              }
              size="small"
              type="number"
              inputProps={{ min: 0, max: 100 }}
              sx={{ width: 80 }}
            />
          </Box>
          <FormHelperText>
            Percentage of recipients who will receive this variant
          </FormHelperText>
        </Box>

        {/* AI Generated Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={selectedVariant.aiPersonalized}
              onChange={(e) =>
                handleVariantFieldChange(
                  selectedVariant.id,
                  "aiPersonalized",
                  e.target.checked
                )
              }
            />
          }
          label="AI Generated"
        />
        <FormHelperText sx={{ mb: 2 }}>
          {actionType === SEQUENCE_ACTION_TYPE_EMAIL
            ? "AI will generate a unique subject line and email body for each recipient using their profile data and your guidance instructions below."
            : "AI will generate a unique message for each recipient using their profile data and your guidance instructions below."}
        </FormHelperText>

        {/* Subject field - only for email when not AI personalized */}
        {actionType === SEQUENCE_ACTION_TYPE_EMAIL && !selectedVariant.aiPersonalized && (
          <TextField
            label="Email Subject"
            fullWidth
            required
            value={selectedVariant.subject || ""}
            onChange={(e) =>
              handleVariantFieldChange(selectedVariant.id, "subject", e.target.value)
            }
            helperText="Subject line for the email"
            sx={{ mb: 2 }}
          />
        )}

        {/* Content field */}
        {actionType === SEQUENCE_ACTION_TYPE_EMAIL && !selectedVariant.aiPersonalized ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Email Content
            </Typography>
            <EmailTemplateEditor
              value={selectedVariant.content}
              subject={selectedVariant.subject}
              onChange={({ subject, content }) => {
                const updates: Partial<SequenceStepVariant> = {};
                if (subject !== undefined) updates.subject = subject;
                if (content !== undefined) updates.content = content;
                const updatedVariants = variants.map((v) =>
                  v.id === selectedVariant.id ? { ...v, ...updates } : v
                );
                onVariantChange(updatedVariants);
              }}
              height={370}
              enableVariables
              enableTemplates
              enableInsertMenu
              enableAIGenerate
              enableSpintax
              enableAISnippet
              enableSpamChecker
              enableMeetingTrackingLink
              enableHeyGenVideo
              enableLandingPage
              schedulingIntegrations={schedulingIntegrations}
              heygenIntegrations={heygenIntegrations}
              customVariableFields={customVariableFields}
              getToken={getToken}
              companyId={companyId}
              sequenceId={sequenceId}
              onAISuccess={onAISuccess}
              onAIError={onAIError}
            />
          </Box>
        ) : !selectedVariant.aiPersonalized ? (
          <Box sx={{ mb: 2 }}>
            <PopOutTextInput
              label={getContentLabel()}
              value={selectedVariant.content}
              onChange={(content: string) =>
                handleVariantFieldChange(selectedVariant.id, "content", content)
              }
              multiline
              rows={4}
            />
          </Box>
        ) : null}

        {/* AI Instructions - only when AI personalized */}
        {selectedVariant.aiPersonalized && (
          <TextField
            label="AI Guidance Instructions"
            value={selectedVariant.aiPersonalizedInstructions || ""}
            onChange={(e) =>
              handleVariantFieldChange(
                selectedVariant.id,
                "aiPersonalizedInstructions",
                e.target.value
              )
            }
            fullWidth
            multiline
            rows={4}
            placeholder="Provide instructions to guide the AI in generating personalized content for this variant..."
            helperText="Tell the AI about the tone, key points, and style you want for this variant"
            sx={{ mb: 2 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ABTestEditor;
